import apiClient from './apiClient';
import { EventSeries, SeriesFormData, APIResponse, PaginatedResponse } from '../types/event';
import { logError } from '../utils/errorLogger';

export interface SeriesFilters {
  search?: string;
  status?: string[];
  category_id?: number;
}

export const seriesAPI = {
  // Get all series (filtered by user role in backend)
  getSeries: async (filters: SeriesFilters = {}): Promise<EventSeries[]> => {
    try {
      const params = new URLSearchParams();
      
      // Add search filter if provided
      if (filters.search) {
        params.append('search', filters.search);
      }
      
      // Add status filter if provided
      if (filters.status && filters.status.length > 0) {
        filters.status.forEach(status => params.append('status', status));
      }
      
      // Add category filter if provided
      if (filters.category_id) {
        params.append('category', filters.category_id.toString());
      }

      const queryString = params.toString();
      const url = `/events/series/${queryString ? `?${queryString}` : ''}`;
      
      const response = await apiClient.get<EventSeries[] | PaginatedResponse<EventSeries>>(
        url,
        { cache: true, ttl: 5 * 60 * 1000 } // Cache for 5 minutes
      );
      
      // Handle Django Rest Framework pagination response  
      if (Array.isArray(response.data)) {
        // Direct array response (no pagination)
        return response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        // Paginated response with results array
        return response.data.results;
      } else {
        logError('Expected array or paginated response', { type: typeof response.data });
        return [];
      }
      
    } catch (error) {
      logError('Series API error', error);
      throw error;
    }
  },

  // Get single series by ID with its events
  getSeriesById: async (seriesId: string): Promise<EventSeries> => {
    const response = await apiClient.get<EventSeries>(
      `/events/series/${seriesId}/`,
      { cache: true, ttl: 5 * 60 * 1000 }
    );
    return response.data;
  },

  // Get events in a specific series
  getSeriesEvents: async (seriesId: string) => {
    const response = await apiClient.get(
      `/events/series/${seriesId}/events/`,
      { cache: true, ttl: 5 * 60 * 1000 }
    );
    return response.data;
  },

  // Create new series with multiple events
  createSeries: async (seriesData: SeriesFormData): Promise<EventSeries> => {
    try {
      const response = await apiClient.post<APIResponse<EventSeries>>(
        '/events/series/',
        seriesData
      );
      
      // Invalidate series cache
      apiClient.invalidateCache('/events/series/');
      apiClient.invalidateCache('/events/'); // Also invalidate events cache
      
      return response.data.data || response.data;
    } catch (error: any) {
      logError('Series creation error', error);
      throw error;
    }
  },

  // Update existing series
  updateSeries: async (seriesId: string, updates: Partial<SeriesFormData>): Promise<EventSeries> => {
    try {
      const response = await apiClient.patch<APIResponse<EventSeries>>(
        `/events/series/${seriesId}/`,
        updates
      );
      
      // Invalidate caches
      apiClient.invalidateCache('/events/series/');
      apiClient.invalidateCache(`/events/series/${seriesId}/`);
      
      return response.data.data || response.data;
    } catch (error: any) {
      logError('Series update error', error);
      throw error;
    }
  },

  // Delete series (and all its events)
  deleteSeries: async (seriesId: string): Promise<void> => {
    try {
      await apiClient.delete(`/events/series/${seriesId}/`);
      
      // Invalidate caches
      apiClient.invalidateCache('/events/series/');
      apiClient.invalidateCache('/events/'); // Also invalidate events cache
      
    } catch (error: any) {
      logError('Series deletion error', error);
      throw error;
    }
  },

  // Add new event to existing series
  addEventToSeries: async (seriesId: string, eventData: any) => {
    try {
      const response = await apiClient.post(
        `/events/series/${seriesId}/add_event/`,
        eventData
      );
      
      // Invalidate caches
      apiClient.invalidateCache('/events/series/');
      apiClient.invalidateCache(`/events/series/${seriesId}/`);
      apiClient.invalidateCache('/events/');
      
      return response.data;
    } catch (error: any) {
      logError('Add event to series error', error);
      throw error;
    }
  },

  // Get series statistics
  getSeriesStats: async (seriesId: string) => {
    try {
      const response = await apiClient.get(
        `/events/series/${seriesId}/stats/`,
        { cache: true, ttl: 2 * 60 * 1000 } // Cache for 2 minutes
      );
      return response.data;
    } catch (error: any) {
      logError('Series stats error', error);
      throw error;
    }
  }
};

export default seriesAPI;
